// ── Nav config — 6 task-based sections (sidebar redesign, 19.10.2026) ────────
// Grouped by task, not by system name. No route changes: every path of the old
// structure is still reachable exactly once (one entry was removed by the owner). Parent entries keep their own link; a sub-entry whose path equals
// its parent's was dropped.

export interface NavSubItem {
  label: string
  icon: string
  path: string
}

export interface NavMainItem {
  label: string
  icon: string
  path: string
  /** Module whose read right shows this entry, when the path is a page of another module (several pages share one module). */
  module?: string
  /** Small heading above the entry; consecutive entries with the same group share it. */
  group?: string
  subItems?: NavSubItem[]
  /** Shown only when the effective role is IT.Admin (server flag isAdmin), not via RoleModuleAccess. */
  adminOnly?: boolean
}

export interface NavSection {
  label: string
  /** Icon of the section in the closed section rail (owner A22: only the 6 section icons). */
  icon: string
  items: NavMainItem[]
}

export const navSections: NavSection[] = [
  {
    label: 'Tagesgeschäft',
    icon: 'today',
    items: [
      {
        label: 'Dashboard',
        icon: 'analytics',
        path: '/',
        subItems: [
          { label: 'Inventar', icon: 'inventory_2',       path: '/inventar' },
          { label: 'Spüler',   icon: 'cleaning_services', path: '/spueler'  },
        ],
      },
      {
        label: 'Command Center',
        icon: 'space_dashboard',
        path: '/betrieb',
        subItems: [
          { label: 'Projekte',        icon: 'folder_supervised', path: '/betrieb/projekte'    },
          { label: 'Standorte',       icon: 'location_on',       path: '/betrieb/standorte'   },
          { label: 'Organigramm',     icon: 'hub',               path: '/betrieb/organigramm' },
          { label: 'Betriebsplanung', icon: 'event_note',        path: '/betrieb/planung'     },
          { label: 'Touren',          icon: 'local_shipping',    path: '/betrieb/touren'      },
        ],
      },
      {
        label: 'Speiseplan',
        icon: 'restaurant_menu',
        path: '/speiseplan',
        subItems: [
          { label: 'Gerichte', icon: 'menu_book', path: '/speiseplan/gerichte' },
        ],
      },
      {
        // HACCP — cold-chain temperature monitoring.
        label: 'HACCP',
        icon: 'device_thermostat',
        path: '/verwaltung/haccp',
      },
    ],
  },
  {
    label: 'Geld & Kosten',
    icon: 'payments',
    items: [
      {
        // Kostenstellen is a deep "home-base" module with its own internal
        // KostenstellenSubNav rail. The global sidebar surfaces the key
        // sub-pages (one click from anywhere); the in-module rail still owns
        // the full set (Übersicht/Stammdaten + these).
        label: 'Kostenstellen',
        icon: 'account_balance',
        path: '/verwaltung/kostenstellen',
        subItems: [
          { label: 'Imports',                icon: 'cloud_upload',   path: '/verwaltung/kostenstellen/imports'      },
          { label: 'Preiskatalog',           icon: 'price_check',    path: '/verwaltung/kostenstellen/preiskatalog' },
          { label: 'Bio-Quote',              icon: 'eco',            path: '/verwaltung/kostenstellen/bio-quote'    },
          { label: 'Auswertung',             icon: 'bar_chart',      path: '/verwaltung/kostenstellen/auswertung'   },
          { label: 'PCA Erlöse',             icon: 'point_of_sale',  path: '/verwaltung/kostenstellen/pca'          },
          { label: 'SAP Kasse',              icon: 'storefront',     path: '/verwaltung/kostenstellen/sap-kasse'    },
          { label: 'Lieferanten-Auswertung', icon: 'local_shipping', path: '/verwaltung/kostenstellen/lieferanten'  },
          { label: 'Re-Food',                icon: 'recycling',      path: '/verwaltung/kostenstellen/refood'       },
        ],
      },
      {
        // Kostenarten — dim.Kostenart hierarchy (Phase 3 Kostenart restructure).
        // IT.Admin only for writes; reads are open to any authenticated user.
        label: 'Kostenarten',
        icon: 'category',
        path: '/verwaltung/kostenarten',
      },
      {
        label: 'Kassenkontrolle',
        icon: 'balance',
        path: '/kasse/kontrolle',
      },
      {
        // Datenquellen — registry of every external system that hands us a
        // school-level identifier (ChefsCulinar Kd.PZ, PCA Schulcode, DATEV
        // Personalkostenstelle, MensaMax …). Not just classical Lieferanten —
        // also payroll + revenue systems. Code path stays /verwaltung/lieferanten.
        label: 'Datenquellen',
        icon: 'database',
        path: '/verwaltung/lieferanten',
        subItems: [
          { label: 'Kategorisierung', icon: 'label', path: '/verwaltung/lieferanten/kategorisierung' },
        ],
      },
    ],
  },
  {
    label: 'Schulen & Kunden',
    icon: 'school',
    items: [
      {
        label: 'Schulübersicht',
        icon: 'school',
        path: '/schulen',
        subItems: [
          { label: 'Hierarchie',         icon: 'device_hub',     path: '/schulen/hierarchie'         },
          { label: 'Geteilte Küchen',    icon: 'restaurant',     path: '/schulen/kuechen'            },
          { label: 'Kalender-Kontrolle', icon: 'calendar_month', path: '/schulen/sperrtag-kontrolle' },
          { label: 'Karten',             icon: 'credit_card',    path: '/schulen/karten'             },
          { label: 'Lieferstellen',      icon: 'pin_drop',       path: '/schulen/lieferstellen'     },
        ],
      },
      {
        // Bestellkorrektur — reconcile a school's Anmeldung file against the PCA
        // roster (school auto-detected); add/remove orders + edit meal-days.
        label: 'Bestellkorrektur',
        icon: 'fact_check',
        path: '/bestellkorrektur',
      },
      {
        // PCA portal analytics. Distinct from "PCA Erlöse" (Kostenstellen →
        // booking revenue) and "PCA · Schülerübersicht" (Support → student matching).
        label: 'Anmeldezahlen (PCA)',
        icon: 'bar_chart_4_bars',
        path: '/pca/anmeldezahlen',
      },
      {
        label: 'Anmeldekontrolle (GigaMail)',
        icon: 'shield',
        path: '/gigamail/anmeldekontrolle',
      },
      {
        label: 'Support',
        icon: 'contact_support',
        path: '/support',
        subItems: [
          { label: 'PCA · Schülerübersicht',  icon: 'child_care',          path: '/support/megatabelle/pca'                  },
          { label: 'MegaTabelle · Datenpull', icon: 'cloud_download',      path: '/support/megatabelle/v2-mockup/datenpull' },
          { label: 'BuT Master',              icon: 'volunteer_activism',  path: '/support/but-master'                       },
          { label: 'Turbo Support',           icon: 'bolt',                path: '/support/turbo'                            },
          { label: 'Auswertung',              icon: 'leaderboard',         path: '/support/auswertung'                       },
        ],
      },
    ],
  },
  {
    // M30 QM: the field app (own entry /qm/) and, for the QM.Leitung role, Übersicht, Kalender, Aufgaben and Nachrichten plus the group "Einrichtung" (as in the prototype boards).
    // The Leitung pages share the module 'qm-leitung', so QM.Kontrolle does not see them.
    label: 'Qualität',
    icon: 'verified',
    items: [
      { label: 'Kontrolle (Feld-App)', icon: 'fact_check', path: '/qm' },
      { label: 'Übersicht',           icon: 'dashboard', path: '/qm-leitung/uebersicht', module: 'qm-leitung' },
      { label: 'Kalender',            icon: 'calendar_month', path: '/qm-leitung/kalender', module: 'qm-leitung' },
      { label: 'Aufgaben',            icon: 'task_alt',  path: '/qm-leitung/aufgaben',   module: 'qm-leitung' },
      { label: 'Nachrichten',         icon: 'chat',      path: '/qm-leitung/nachrichten', module: 'qm-leitung' },
      { label: 'Checkliste',          icon: 'checklist', path: '/qm-leitung/checkliste', module: 'qm-leitung', group: 'Einrichtung' },
      { label: 'Positionen & Küchen', icon: 'groups',    path: '/qm-leitung/positionen', module: 'qm-leitung', group: 'Einrichtung' },
      { label: 'Intervall',           icon: 'timer',     path: '/qm-leitung/intervall',  module: 'qm-leitung', group: 'Einrichtung' },
    ],
  },
  {
    label: 'Team',
    icon: 'groups',
    items: [
      {
        // Mitarbeiterportal — M26 admin surface for mein.mammascanteen.de,
        // split into four sections (each its own registry module, gated
        // independently via ops.RoleModuleAccess). The group's own path
        // points at Schulungen — the flagship section, and where
        // /verwaltung/mitarbeiterportal redirects (router.tsx) — while
        // Einstellungen stays IT.Admin-only and hides itself for everyone
        // else via canRead.
        label: 'Mitarbeiterportal',
        icon: 'badge',
        path: '/verwaltung/mitarbeiterportal/schulungen',
        subItems: [
          { label: 'Dokumente',     icon: 'description', path: '/verwaltung/mitarbeiterportal/dokumente'     },
          { label: 'Mitteilungen',  icon: 'campaign',    path: '/verwaltung/mitarbeiterportal/mitteilungen'  },
          { label: 'Einstellungen', icon: 'settings',    path: '/verwaltung/mitarbeiterportal/einstellungen' },
        ],
      },
      {
        // Personal — HR (owner A4: shown in Team, no longer in the Lab).
        label: 'Personal',
        icon: 'groups',
        path: '/hr',
        subItems: [
          { label: 'Personalübersicht', icon: 'event_available', path: '/hr/dashboard'           },
          { label: 'Zeiterfassung',     icon: 'schedule',        path: '/zeiterfassung'          },
          { label: 'Onboarding',        icon: 'person_add',      path: '/hr/onboarding'          },
          { label: 'Vertragsvorlagen',  icon: 'assignment',      path: '/hr/contracts/templates' },
        ],
      },
      { label: 'Gastromatic Importliste', icon: 'upload_file', path: '/gastromatic' },
    ],
  },
  {
    label: 'IT & Admin',
    icon: 'admin_panel_settings',
    items: [
      {
        label: 'IT-Übersicht',
        icon: 'terminal',
        path: '/it',
        subItems: [
          { label: 'FleetOps',        icon: 'devices',              path: '/it/fleetops'               },
          { label: 'IT Helpdesk',     icon: 'computer',             path: '/it/helpdesk'               },
          { label: 'Schulportal-Module', icon: 'toggle_on',         path: '/it/schulportal-module'     },
          { label: 'Compliance',      icon: 'verified_user',        path: '/it/compliance'             },
          { label: 'Tech Stack',      icon: 'lan',                  path: '/it/compliance/tech-stack'  },
          { label: 'Rollen & Rechte', icon: 'admin_panel_settings', path: '/it/role-admin'             },
        ],
      },
      { label: 'Unternehmen',          icon: 'policy',     path: '/admin/firma-policy'      },
      // Owner A5: moved here from the Lab.
      { label: 'Schuljahr & Ferien',   icon: 'date_range', path: '/admin/schuljahr-policy'  },
      { label: 'Event Log',            icon: 'history',    path: '/admin/eventlog'          },
    ],
  },
  {
    // ── Lab & Vorlagen ─────────────────────────────────────────────────
    // Modules registered in registry.ts but not in the main IA. Surface
    // here so IT.Admin can discover everything without memorizing URLs.
    label: 'Lab & Vorlagen',
    icon: 'science',
    items: [
      { label: 'KPI-Einstellungen',     icon: 'tune',            path: '/verwaltung/einstellungen/kpis'     },
      { label: 'Lieferanten-Workbench', icon: 'linked_services', path: '/verwaltung/lieferanten/workbench'  },
      {
        label: 'Motion-Templates',
        icon: 'palette',
        path: '/design/motion-1',
        subItems: [
          { label: 'Motion Assets', icon: 'precision_manufacturing', path: '/design/motion-2' },
          { label: 'Liquid',        icon: 'auto_awesome',            path: '/design/liquid'   },
        ],
      },
      {
        label: 'Glass Templates',
        icon: 'flare',
        path: '/design/glass-1',
        subItems: [
          { label: 'Project Ops', icon: 'schema', path: '/design/glass-2' },
        ],
      },
      { label: 'Components', icon: 'widgets', path: '/design/components' },
      // Owner A19: pages with a route but no menu entry — IT.Admin only.
      { label: 'Schüler & PCA',        icon: 'child_care',    path: '/support/schueler',                          adminOnly: true },
      { label: 'Schulungs-Dashboard',  icon: 'monitoring',    path: '/verwaltung/mitarbeiterportal/dashboard',    adminOnly: true },
      { label: 'Import-Prüfung',       icon: 'rule',          path: '/verwaltung/kostenstellen/imports/pruefung', adminOnly: true },
      { label: 'DATEV-Import',         icon: 'request_quote', path: '/verwaltung/kostenstellen/imports/datev',    adminOnly: true },
    ],
  },
]
